import { useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { logout, tokenRefreshed } from "../store/slices/authSlice";
import { selectIsAuthenticated } from "../store/selectors/authSelectors";
import { authService } from "../services/authService";
import { useModal } from "../components/Modal/ModalProvider";
import { STORAGE_KEYS, COOKIE_KEYS, ROUTES } from "../constants";
import { TokenDto } from "../types/api";
import { getCookie, setCookie } from "../utils/cookieUtils";
import {
  listenToAuthEvents,
  broadcastLogout,
  broadcastTokenRefresh,
  clearAuthData,
  tryBecomeLeader,
  isLeader,
  updateLeaderHeartbeat,
  releaseLeadership,
} from "../utils/authSync";

interface TokenWithTimestamp extends TokenDto {
  issued_at?: number;
}

const LEADER_HEARTBEAT_INTERVAL = 3000; // 3 seconds
const LEADER_CHECK_INTERVAL = 5000; // 5 seconds - how often followers check if leader is alive

/**
 * Hook to manage token lifecycle with Leader Election:
 * - Only one tab (leader) refreshes tokens
 * - Other tabs sync from localStorage when leader broadcasts refresh
 */
export const useTokenManager = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const { closeAllModals } = useModal();
  const refreshTimerRef = useRef<NodeJS.Timeout | null>(null);
  const heartbeatTimerRef = useRef<NodeJS.Timeout | null>(null);
  const leaderCheckTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isRefreshingRef = useRef<boolean>(false);
  const hasInitializedRef = useRef<boolean>(false);

  // Calculate time until token expires (in milliseconds)
  const getTimeUntilExpiry = (tokenData: TokenWithTimestamp): number => {
    if (!tokenData?.issued_at || !tokenData?.expires_in) {
      return 0;
    }

    const issuedAt = tokenData.issued_at * 1000;
    const expiresIn = tokenData.expires_in * 1000;
    const expiryTime = issuedAt + expiresIn;
    const now = Date.now();
    return expiryTime - now;
  };

  const getTokenFromStorage = useCallback((): TokenWithTimestamp | null => {
    try {
      const tokenRaw = getCookie(COOKIE_KEYS.ACCESS_TOKEN);
      if (!tokenRaw) return null;

      const token = JSON.parse(tokenRaw) as TokenWithTimestamp;

      const refreshToken = getCookie(COOKIE_KEYS.REFRESH_TOKEN);
      if (refreshToken) {
        token.refresh_token = refreshToken;
      }

      if (!token.refresh_token) return null;

      if (!token.issued_at && token.expires_in) {
        token.issued_at = Date.now() / 1000;
        const { refresh_token: _rt, ...accessData } = token;
        setCookie(
          COOKIE_KEYS.ACCESS_TOKEN,
          JSON.stringify({ ...accessData, issued_at: token.issued_at }),
          1,
        );
      }

      return token;
    } catch (e) {
      console.error("[useTokenManager] Failed to parse token from cookie:", e);
    }
    return null;
  }, []);

  // Handle logout
  const handleLogout = useCallback(() => {
    const isRegistrationFlow = localStorage.getItem(
      STORAGE_KEYS.REGISTRATION_TOKEN,
    );
    if (isRegistrationFlow) {
      console.log("Skipping logout during registration flow");
      return;
    }

    closeAllModals();
    releaseLeadership();
    clearAuthData();
    dispatch(logout());
    broadcastLogout();

    if (location.pathname !== ROUTES.LOGIN) {
      navigate(ROUTES.LOGIN, { replace: true });
    }
  }, [dispatch, navigate, location.pathname, closeAllModals]);

  // Sync token from localStorage to Redux
  const syncTokenFromStorage = useCallback(() => {
    const storedToken = getTokenFromStorage();
    if (storedToken) {
      dispatch(tokenRefreshed({ token: storedToken }));
      return storedToken;
    }
    return null;
  }, [dispatch, getTokenFromStorage]);

  // Refresh token - only called by leader
  const refreshToken = useCallback(
    async (
      tokenData: TokenWithTimestamp,
    ): Promise<TokenWithTimestamp | null> => {
      if (isRefreshingRef.current) {
        return null;
      }

      // Double-check we're still the leader
      if (!isLeader()) {
        return syncTokenFromStorage();
      }

      isRefreshingRef.current = true;

      try {
        const newToken = await authService.refreshToken({
          access_token: tokenData.access_token,
          refresh_token: tokenData.refresh_token,
        });

        const newTokenWithTimestamp = {
          ...newToken,
          issued_at: Date.now() / 1000,
        };

        const { refresh_token: newRt, ...newAccessData } =
          newTokenWithTimestamp;
        setCookie(COOKIE_KEYS.ACCESS_TOKEN, JSON.stringify(newAccessData), 1);
        setCookie(COOKIE_KEYS.REFRESH_TOKEN, newRt, 7);

        dispatch(tokenRefreshed({ token: newTokenWithTimestamp }));

        // Broadcast to other tabs
        broadcastTokenRefresh();

        return newTokenWithTimestamp;
      } catch (error) {
        console.error("[useTokenManager] Token refresh failed:", error);
        handleLogout();
        return null;
      } finally {
        isRefreshingRef.current = false;
      }
    },
    [dispatch, handleLogout, syncTokenFromStorage],
  );

  // Schedule token refresh - only if leader
  const scheduleTokenRefresh = useCallback(
    (tokenData: TokenWithTimestamp) => {
      // Clear any existing timer
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }

      // Only leader schedules refresh
      if (!isLeader()) {
        return;
      }

      const timeUntilExpiry = getTimeUntilExpiry(tokenData);

      if (timeUntilExpiry <= 0) {
        handleLogout();
        return;
      }

      // Refresh 10 seconds before expiration
      const REFRESH_BEFORE_EXPIRY = 10000;
      const refreshTime = Math.max(0, timeUntilExpiry - REFRESH_BEFORE_EXPIRY);

      if (refreshTime <= 0) {
        // Refresh immediately
        refreshToken(tokenData).then((newToken) => {
          if (newToken) {
            scheduleTokenRefresh(newToken);
          }
        });
      } else {
        refreshTimerRef.current = setTimeout(() => {
          refreshTimerRef.current = null;
          // Re-check leadership before refresh
          if (isLeader()) {
            refreshToken(tokenData).then((newToken) => {
              if (newToken) {
                scheduleTokenRefresh(newToken);
              }
            });
          }
        }, refreshTime);
      }
    },
    [handleLogout, refreshToken],
  );

  // Start leader heartbeat
  const startHeartbeat = useCallback(() => {
    if (heartbeatTimerRef.current) {
      clearInterval(heartbeatTimerRef.current);
    }

    heartbeatTimerRef.current = setInterval(() => {
      if (isLeader()) {
        updateLeaderHeartbeat();
      }
    }, LEADER_HEARTBEAT_INTERVAL);
  }, []);

  // Initialize when authenticated
  useEffect(() => {
    // Skip if not authenticated
    if (!isAuthenticated) {
      hasInitializedRef.current = false;
      // Clear any lingering timers so they cannot fire after logout regardless
      // of which logout path was used (inactivity, manual, cross-tab, etc.)
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
      if (heartbeatTimerRef.current) {
        clearInterval(heartbeatTimerRef.current);
        heartbeatTimerRef.current = null;
      }
      return;
    }

    // Skip if already initialized
    if (hasInitializedRef.current) return;

    const storedToken = getTokenFromStorage();
    if (!storedToken) return;

    hasInitializedRef.current = true;

    // Try to become leader
    const becameLeader = tryBecomeLeader();

    if (becameLeader) {
      startHeartbeat();
      scheduleTokenRefresh(storedToken);
    }
  }, [
    isAuthenticated,
    getTokenFromStorage,
    startHeartbeat,
    scheduleTokenRefresh,
  ]);

  // Handle visibility change - separate effect
  useEffect(() => {
    if (!isAuthenticated) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        const currentToken = getTokenFromStorage();
        if (!currentToken) return;

        // Only try to become leader if not already leader
        if (isLeader()) return;

        const becameLeader = tryBecomeLeader();
        if (becameLeader) {
          startHeartbeat();
          scheduleTokenRefresh(currentToken);
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [
    isAuthenticated,
    getTokenFromStorage,
    startHeartbeat,
    scheduleTokenRefresh,
  ]);

  // Periodically check if leader is alive (for followers)
  useEffect(() => {
    if (!isAuthenticated) return;

    // Clear any existing timer
    if (leaderCheckTimerRef.current) {
      clearInterval(leaderCheckTimerRef.current);
      leaderCheckTimerRef.current = null;
    }

    // If we're already the leader, no need to check
    if (isLeader()) return;

    // Start periodic check for leader status
    leaderCheckTimerRef.current = setInterval(() => {
      // Skip if we're already the leader
      if (isLeader()) {
        if (leaderCheckTimerRef.current) {
          clearInterval(leaderCheckTimerRef.current);
          leaderCheckTimerRef.current = null;
        }
        return;
      }

      // Try to become leader (will succeed if current leader's heartbeat timed out)
      const becameLeader = tryBecomeLeader();
      if (becameLeader) {
        const currentToken = getTokenFromStorage();
        if (currentToken) {
          startHeartbeat();
          scheduleTokenRefresh(currentToken);
        }
        // Stop checking since we're now the leader
        if (leaderCheckTimerRef.current) {
          clearInterval(leaderCheckTimerRef.current);
          leaderCheckTimerRef.current = null;
        }
      }
    }, LEADER_CHECK_INTERVAL);

    return () => {
      if (leaderCheckTimerRef.current) {
        clearInterval(leaderCheckTimerRef.current);
        leaderCheckTimerRef.current = null;
      }
    };
  }, [
    isAuthenticated,
    getTokenFromStorage,
    startHeartbeat,
    scheduleTokenRefresh,
  ]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
      if (heartbeatTimerRef.current) {
        clearInterval(heartbeatTimerRef.current);
        heartbeatTimerRef.current = null;
      }
      if (leaderCheckTimerRef.current) {
        clearInterval(leaderCheckTimerRef.current);
        leaderCheckTimerRef.current = null;
      }
    };
  }, []);

  // Release leadership on page unload
  useEffect(() => {
    const handleUnload = () => {
      releaseLeadership();
    };

    window.addEventListener("beforeunload", handleUnload);
    return () => {
      window.removeEventListener("beforeunload", handleUnload);
      releaseLeadership();
    };
  }, []);

  // Listen for auth events from other tabs
  useEffect(() => {
    const cleanup = listenToAuthEvents({
      onLogout: () => {
        const isRegistrationFlow = localStorage.getItem(
          STORAGE_KEYS.REGISTRATION_TOKEN,
        );
        if (isRegistrationFlow) return;

        closeAllModals();
        clearAuthData();
        dispatch(logout());

        if (location.pathname !== ROUTES.LOGIN) {
          navigate(ROUTES.LOGIN, { replace: true });
        }
      },
      onLogin: () => {
        syncTokenFromStorage();
      },
      onTokenRefreshed: () => {
        // Cancel our refresh timer if we had one
        if (refreshTimerRef.current) {
          clearTimeout(refreshTimerRef.current);
          refreshTimerRef.current = null;
        }

        // Sync the new token from localStorage
        const newToken = syncTokenFromStorage();

        // If we're leader somehow, reschedule (shouldn't happen normally)
        if (newToken && isLeader()) {
          scheduleTokenRefresh(newToken);
        }
      },
    });

    return cleanup;
  }, [
    dispatch,
    navigate,
    location.pathname,
    closeAllModals,
    syncTokenFromStorage,
    scheduleTokenRefresh,
  ]);

  return { handleLogout };
};
